import {Component, Input, ViewEncapsulation, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeWhile,

} from 'rxjs/operators';

import {OrderItemDelivery} from '../../../../models/order-item-delivery.model';
import {OrderItem} from '../../../../models/order-item.model';
import {ProductService} from '../../../../services/product.service';
import {AllocationGroup, AllocationQty, InventoryAllocation} from '../../../../interfaces/allocation-group';

import {isEmptyArray} from '../../../../shared/utils';

@Component({
    selector: 'app-on-demand-inventory-allocation-control',
    templateUrl: './on-demand-inventory-allocation-control.component.html',
    styleUrls: ['./on-demand-inventory-allocation-control.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class OnDemandInventoryAllocationControlComponent implements  OnInit, OnDestroy {

  @Input() orderItem: OrderItem;
  @Input() delivery: OrderItemDelivery;
  @Input() readonly = false;
  @Input() disabled  = false;
  // 'list' is the All Products list view column, which is too narrow to carry the field label and
  // needs the sticky-column z-index the list cell styles hang off .product-list--view.
  @Input() view: 'grid' | 'list' = 'grid';

  @Output() onSelectAllocationGroup =  new EventEmitter<AllocationQty>();

  searchSubject: Subject<string> = new Subject<string>();
  filteredAllocationGroups$: Observable<AllocationQty[]>;
  selectedInventoryAllocationId: number;

  private isAlive = true;
  inventoryAllocation$: Observable<InventoryAllocation>;
  fetchingAllocationInventory$: Observable<boolean>;
  allocationsFailed$: Observable<boolean>;

  constructor(private productService: ProductService) {
  }

  ngOnInit() {
    this.inventoryAllocation$ = this.getInventoryAllocation$();
    this.fetchingAllocationInventory$ = this.productService.isFetchingProductAllocations$(this.orderItem?.product?.id);
    this.allocationsFailed$ = this.productService.isProductAllocationsFailed$(this.orderItem?.product?.id);
    // search by label in dropdown
    this.filteredAllocationGroups$ = this.searchSubject
      .pipe(
        takeWhile(() => this.isAlive),
        debounceTime(200),
        distinctUntilChanged(),
        startWith(''),
        switchMap(term => {

          return this.inventoryAllocation$.pipe(
            map(inventoryAllocation => {
                if (!inventoryAllocation) {
                  return [];
                }

                if (!term) {
                  // return all
                  return inventoryAllocation.allocation_qty || [];
                }

                // filter by term
                return (inventoryAllocation.allocation_qty || []).filter(g =>
                  g.allocation_group_label.toLowerCase().includes(term.toLowerCase())
                ) || [];
            })
          );
        }),
      );

      this.setSelectedInventoryAllocationId();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }


  get isListView(): boolean {
    return this.view === 'list';
  }

  /**
   * The list view renders this control inside .table-wrapper, which is overflow:auto - a clipping
   * ancestor no z-index can escape, so a panel left in place is cut off by the table. Appending to
   * body takes it out of the table entirely; ng-select keeps it aligned by listening for scroll in
   * the capture phase, so it still follows the table as the user scrolls.
   *
   * Grid view has no such ancestor and is left exactly as it was.
   */
  get appendTo(): string | null {
    return this.isListView ? 'body' : null;
  }

  // The two classes are orthogonal: ng-select--width-constrained is what stops a long group label
  // from sizing the control by its content, and the list column is narrower than the grid, so it
  // needs that more, not less. product-list--view only carries the dropdown panel's z-index.
  get selectClass(): string {
    return this.isListView
      ? 'ng-select--width-constrained product-list--view'
      : 'ng-select--width-constrained';
  }

  get enabled(): boolean {
    return this.orderItem.product.isBuyingWindow !== true && this.orderItem.product.isEnabledAllocatedInventory === true;
  }

  protected onSelect(inventory: AllocationQty) {
    if (!inventory) {
      return;
    }
    this.selectedInventoryAllocationId = inventory.allocation_group_id
    this.onSelectAllocationGroup.emit(inventory);
  }



  get isBuyingWindow(): boolean {
    return this.orderItem?.product.isBuyingWindow;
  }


  get hasVariations(): boolean {
    return this.orderItem?.product.hasVariations;
  }


  get someDeliveriesHaveAllocationGroup(): boolean {

    if (this.orderItem?.product.isBuyingWindow === true) {
      // this should render only on-demand products
      return false;
    }

    if (!this.readonly) {
      return this.orderItem?.product.isEnabledAllocatedInventory;
    }   else {
      return this.orderItem?.product.isEnabledAllocatedInventory;
    }
  }

  getInventoryAllocation$(): Observable<InventoryAllocation> {
    if (!this.enabled || this.isBuyingWindow) {
      return of(null);
    }
    return this.productService.getInventoryAllocationForSku(this.orderItem?.product?.id);
  }

  get allocationGroupWithLoading$(): Observable<{ loading: boolean; value: AllocationGroup | null }> {
    if (!this.delivery?.allocation_group_id) {
      return of({ loading: false, value: null });
    }

    return this.productService.getAllocationGroupById(this.delivery?.allocation_group_id).pipe(
      map(value => ({ loading: false, value })),
      startWith({ loading: true, value: null })
    );
  }

  get hasSelectedAllocationGroup(): boolean {
    return !!this.delivery?.allocation_group_id;

  }


  private setSelectedInventoryAllocationId() {
    if (this.orderItem?.product.isBuyingWindow === true) {
      return;
    }

    if (!this.orderItem.product.isEnabledAllocatedInventory) {
      return;
    }

    const firstSkuId = this.orderItem?.product?.skus?.[0]?.id;
    if (!firstSkuId) {
      // not found
      return;
    }

    if (isEmptyArray(this.delivery?.allocation_groups)) {
      return;
    }

    this.selectedInventoryAllocationId = this.delivery.allocation_groups.find( ag => ag.sku_id === firstSkuId)?.allocation_group_id;

  }

  /**
   * What the closed control shows. When the selected group is not among the loaded items - it was
   * revoked, the product no longer holds any of it, or the request failed - ng-select wraps the raw
   * ngModel value in an item with no fields, and the template would print a blank name. The label
   * saved on the delivery still knows what was picked, so it answers instead.
   */
  labelForSelected(item: AllocationQty): string {
    return item?.allocation_group_label || this.selectedAllocationGroupLabel;
  }

  /** Only a real item carries a quantity; a placeholder would render a bare "Available:". */
  hasAvailableQty(item: AllocationQty): boolean {
    return typeof item?.available_qty === 'number';
  }

  get selectedAllocationGroupLabel(): string {
    if (!this.selectedInventoryAllocationId) {
      return '';
    }

    const allocationGroup = this.delivery?.allocation_groups?.find( ag => ag.allocation_group_id === this.selectedInventoryAllocationId);
    return allocationGroup?.allocation_group_label || '';
  }

}
